import { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import Box from "@mui/material/Box";
import Container from "@mui/material/Container";
import Grid from "@mui/material/Grid";

import { get } from "lib/api";
import { firstListMediaUrl, firstThumbnailMediaUrl } from "lib/media";
import { useAuth } from "context/AuthContext";
import { useCart } from "context/CartContext";
import { currencyForCompany } from "lib/companyProfile";
import { getTextLocale } from "lib/storefront";
import useStorefrontCopy from "hooks/useStorefrontCopy";
import StorefrontLayout from "../components/StorefrontLayout";
import ProductCard from "../components/ProductCard";
import VbsIcon from "../components/VbsIcon";
import { VBS, normalizeTransmissions, resolvePrice, resetButton, labelCaps } from "../theme";

export default function CatalogPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const q = String(searchParams.get("q") || "");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [selectedTransmission, setSelectedTransmission] = useState("");
  const { t } = useStorefrontCopy();

  const isMobileRoute =
    location.pathname === "/m/catalog" ||
    location.pathname.startsWith("/m/catalog/");
  const toPath = (path) =>
    isMobileRoute ? `/m${path === "/" ? "" : path}` : path;

  const { addItem } = useCart();
  const { token, activeCompany, storefrontTenant } = useAuth();
  const canOrder = !!token;
  const companyKey = String(storefrontTenant || activeCompany || "vbsus").toLowerCase();
  const textLocale = getTextLocale();

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(q), 300);
    return () => clearTimeout(timer);
  }, [q]);

  const { data, isLoading } = useQuery({
    queryKey: [
      "vbsus-catalog",
      debouncedSearch,
      companyKey,
      textLocale,
      token ? "auth" : "public",
    ],
    queryFn: () =>
      get("/api/storefront/catalog", {
        q: debouncedSearch || undefined,
        sort: "name:asc",
        pagination: { pageSize: 100 },
      }),
    staleTime: 30000,
  });

  const currency =
    data?.meta?.company?.baseCurrency || currencyForCompany(companyKey);

  const products = useMemo(
    () =>
      (data?.data || []).map((item) => {
        const attrs = item.attributes || item;
        return {
          id: item.id,
          documentId: item.documentId || item.id,
          sku: attrs.sku,
          name: attrs.name,
          images: attrs.images,
          unitPrice: attrs.unitPrice,
          coreCharge: attrs.coreCharge,
          priceLevels: attrs.priceLevels,
          status: attrs.status,
          transmissions: normalizeTransmissions(attrs.transmissions),
        };
      }),
    [data],
  );

  const transmissionOptions = useMemo(() => {
    const set = new Set();
    products.forEach((product) =>
      (product.transmissions || []).forEach((trans) => set.add(trans)),
    );
    return Array.from(set).sort();
  }, [products]);

  const visibleProducts = useMemo(() => {
    if (!selectedTransmission) return products;
    return products.filter((product) =>
      (product.transmissions || []).some(
        (trans) => trans.toLowerCase() === selectedTransmission.toLowerCase(),
      ),
    );
  }, [products, selectedTransmission]);

  const openProduct = (product) => navigate(toPath(`/catalog/${product.documentId}`));
  const addProduct = (product) => {
    const price = Number(resolvePrice(product) || 0);
    if (!price) return;
    addItem(
      {
        documentId: product.documentId,
        sku: product.sku,
        name: product.name,
        unitPrice: price,
        coreCharge: product.coreCharge != null ? Number(product.coreCharge) : null,
        imageUrl: firstThumbnailMediaUrl(product?.images) || firstListMediaUrl(product?.images),
      },
      1,
    );
  };

  const setSearch = (value) => {
    const next = String(value || "").trim();
    if (next) setSearchParams({ q: next });
    else setSearchParams({});
  };

  return (
    <StorefrontLayout maxWidth={null}>
      {/* Full-width search banner — not in header like EDS */}
      <Box>
        <Box
          sx={{
            bgcolor: VBS.ink,
            color: "#fff",
            py: { xs: 3, md: 4 },
          }}
        >
          <Container maxWidth="lg">
            {!isMobileRoute ? (
              <>
                <Box sx={{ ...labelCaps, color: VBS.accentBright, mb: 0.75 }} component="p">
                  {t("common.productCatalog", "Product catalog")}
                </Box>
                <Box
                  component="h1"
                  sx={{
                    m: 0,
                    fontSize: { xs: "1.75rem", md: "2.25rem" },
                    fontWeight: 800,
                    letterSpacing: "-0.02em",
                  }}
                >
                  {t("catalog.title", "Find the right valve body.")}
                </Box>
              </>
            ) : null}
            <Box
              sx={{
                mt: isMobileRoute ? 0 : 2.5,
                display: "flex",
                alignItems: "stretch",
                bgcolor: VBS.surface,
                borderRadius: `${VBS.radiusMd}px`,
                overflow: "hidden",
                border: `2px solid ${VBS.lineStrong}`,
              }}
            >
              <Box sx={{ display: "grid", placeItems: "center", px: 1.5, color: VBS.muted }}>
                <VbsIcon name="search" size={22} color={VBS.muted} />
              </Box>
              <Box
                component="input"
                value={q}
                onChange={(e) => setSearch(e.target.value)}
                placeholder={t("common.searchPlaceholder", "Search valve bodies...")}
                sx={{
                  flex: 1,
                  border: 0,
                  outline: "none",
                  py: 1.5,
                  pr: 2,
                  fontSize: "1rem",
                  fontWeight: 500,
                  color: VBS.ink,
                  fontFamily: "inherit",
                  bgcolor: "transparent",
                  "&::placeholder": { color: VBS.subtle },
                }}
              />
            </Box>
            <Box sx={{ mt: 1.25, fontSize: "0.8125rem", color: "rgba(255,255,255,0.6)" }}>
              {isLoading
                ? t("common.loadingInventory", "Loading inventory…")
                : t("common.listingCount", "{count} listing(s)").replace(
                    "{count}",
                    String(visibleProducts.length),
                  )}
            </Box>
          </Container>
        </Box>
        <Box sx={{ height: 4, background: VBS.swoosh }} />
      </Box>

      {/* Horizontal chip filters — EDS uses left sidebar */}
      {transmissionOptions.length ? (
        <Box
          sx={{
            bgcolor: VBS.surface,
            borderBottom: `1px solid ${VBS.lineStrong}`,
            py: 1.5,
          }}
        >
          <Container maxWidth="xl">
            <Box
              sx={{
                display: "flex",
                gap: 1,
                overflowX: "auto",
                pb: 0.5,
                "&::-webkit-scrollbar": { height: 4 },
              }}
            >
              {["", ...transmissionOptions].map((transmission) => {
                const active = selectedTransmission === transmission;
                const label = transmission || t("common.allTransmissions", "All transmissions");
                return (
                  <Box
                    key={label}
                    component="button"
                    type="button"
                    onClick={() => setSelectedTransmission(transmission)}
                    sx={{
                      ...resetButton,
                      flexShrink: 0,
                      px: 1.75,
                      py: 0.75,
                      borderRadius: `${VBS.radius}px`,
                      border: `1px solid ${active ? VBS.accent : VBS.lineStrong}`,
                      bgcolor: active ? VBS.accentSoft : VBS.panel,
                      color: active ? VBS.accent : VBS.muted,
                      fontSize: "0.75rem",
                      fontWeight: 700,
                      letterSpacing: "0.04em",
                      textTransform: "uppercase",
                    }}
                  >
                    {label}
                  </Box>
                );
              })}
            </Box>
          </Container>
        </Box>
      ) : null}

      <Container maxWidth="xl" sx={{ py: { xs: 4, md: 5 } }}>
        {isLoading ? (
          <Box sx={{ py: 10, textAlign: "center", color: VBS.muted }}>
            {t("common.loadingCatalog", "Loading catalog…")}
          </Box>
        ) : visibleProducts.length === 0 ? (
          <Box
            sx={{
              py: 10,
              textAlign: "center",
              color: VBS.muted,
              bgcolor: VBS.surface,
              border: `1px dashed ${VBS.lineStrong}`,
              borderRadius: `${VBS.radiusMd}px`,
            }}
          >
            {t("catalog.empty", "No products match your search.")}
          </Box>
        ) : (
          <Grid container spacing={2.5}>
            {visibleProducts.map((product) => (
              <Grid item xs={12} sm={6} lg={4} key={product.documentId}>
                <ProductCard
                  product={product}
                  currency={currency}
                  onOpen={() => openProduct(product)}
                  onAdd={addProduct}
                  canOrder={canOrder}
                />
              </Grid>
            ))}
          </Grid>
        )}
      </Container>
    </StorefrontLayout>
  );
}
